import { useState } from 'react';
import { t, Trans } from '@lingui/macro';
import _ from 'lodash';
import Button from '@material-ui/core/Button';

import { SELECTIONMODE_MULTI } from '../../data-table/SelectionContext';
import DataTable from '../../data-table/DataTable';
import { useQueryLoader } from '../../data-table/Loaders';
import AdminPage from '../AdminPage';
import Actions from '../../page-framework/actions/Actions';
import { useFormDeleteService, useFormDeleteWrapper } from '../../../services/FormDeleteService';
import ConfirmDeleteButton from '../../form/ConfirmDeleteButton';
import { SORT_ASC } from '../../../utilities/FormUtilities';
import Link from '../../links/Link';
import {
    AdminPages,
    buildCreatePageTarget,
    buildPageId,
    buildPageTarget,
    buildRootPageTarget,
} from '../AdminLinkUtils';
import {
    CITY_FIELD,
    COUNTRY_FIELD,
    DESCRIPTION_FIELD,
    MATCHING_AGENTS_FIELD,
    MATCHING_AGENTS_SORTABLE_FIELD,
    NAME_FIELD,
    NOTES_FIELD,
    PROVINCE_FIELD,
    TAGS_FIELD,
} from './SiteAdminConstants';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { renderMatchingAgents } from './SiteAdminHelper';
import { useColumnStyles } from './SiteAdminStyles';
import ImportDialog from './ImportDialog';
import { useAssignLocation } from '../../../utilities/PgmUtilities';
import useUiToasts, { createToastConfig, INFO } from '../../../stores/ToastStore';
import { useIntervalRefreshTrigger } from '../../data-table/Triggers';
import { formatTags } from '../../../utilities/StringUtilities';
import { useIsIpmDisabled } from '../../../stores/EnvironmentStore';

const functionName = AdminPages.Sites;

function getColumns(classes) {
    return [
        {
            type: COLUMN_TYPES.LINK,
            mapping: NAME_FIELD,
            title: t`Name`,
            sortable: true,
            width: 300,
            link: {
                target: buildPageTarget(functionName),
            },
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: DESCRIPTION_FIELD,
            title: t`Description`,
            sortable: true,
            width: 400,
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: MATCHING_AGENTS_FIELD,
            title: t`nPoints`,
            sortable: true,
            sortProperty: MATCHING_AGENTS_SORTABLE_FIELD,
            width: 200,
            className: classes.root,
            csvFormatter: formatTags,
            renderer: renderMatchingAgents,
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: NOTES_FIELD,
            title: t`Notes`,
            sortable: true,
            flex: 1,
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: TAGS_FIELD,
            title: t`Tags`,
            sortable: false,
            csvFormatter: formatTags,
            formatter: formatTags,
            width: 300,
        },
    ];
}

const initialSort = [{ property: NAME_FIELD, direction: SORT_ASC }];

const dataSource = {
    url: '/query/admin/sites',
    crudUrl: '/ipm/v1/admin/sites',
    totalProperty: 'count',
    dataProperty: 'data',
    params: {
        searchFields: [
            NAME_FIELD,
            DESCRIPTION_FIELD,
            COUNTRY_FIELD,
            PROVINCE_FIELD,
            CITY_FIELD,
            NOTES_FIELD,
            MATCHING_AGENTS_SORTABLE_FIELD,
            TAGS_FIELD,
        ],
    },
};

const exportToastMessage = t`Your file is being generated and will download when it is ready.`;

export default function SiteAdminList() {
    const classes = useColumnStyles();
    const { deleteWrapper } = useFormDeleteWrapper();
    const [actionsDisabled, setActionsDisabled] = useState(false);
    const [tableState, setTableState] = useState(undefined);
    const [tableSelection, setTableSelection] = useState(undefined);
    const formDeleteService = useFormDeleteService();
    const [, uiToastActions] = useUiToasts();
    const assignLocation = useAssignLocation();
    const { triggerRef, trigger: refresher } = useIntervalRefreshTrigger();
    const [isIpmDisabled] = useIsIpmDisabled();

    // Used by the data table to load data.
    const loader = useQueryLoader(dataSource, { disableFilterState: true });

    const handleConfirmedDelete = () => {
        const params = _.merge(
            {
                search: tableState?.search || '',
            },
            dataSource.params
        );

        return deleteWrapper({
            deleteFunction: () =>
                formDeleteService.deleteSelection(`${dataSource.crudUrl}/selection/softDelete`, params, tableSelection),
            refresherFunc: refresher,
        })();
    };

    const handleTableSelectionChange = (currentTableSelection) => {
        setTableSelection(currentTableSelection);
        setActionsDisabled(
            !currentTableSelection || (_.isEmpty(currentTableSelection.selection) && !currentTableSelection.inverted)
        );
    };

    const handleExport = () => {
        const selection = tableSelection?.selection;
        let inverted = tableSelection?.inverted;
        if (selection.length === 0 && inverted === false) {
            inverted = true;
        }
        const selectionParams = JSON.stringify({ selection, inverted });
        uiToastActions.createToast(createToastConfig(INFO, exportToastMessage));
        assignLocation(`/ipm/v1c/sitesExport/exportCSV?t=${Date.now()}&selectionParams=${selectionParams}`);
    };

    return (
        <AdminPage
            title={t`Sites`}
            pageId={buildPageId(functionName)}
            actions={
                <Actions>
                    <ConfirmDeleteButton
                        disabled={actionsDisabled}
                        handleConfirmedDelete={handleConfirmedDelete}
                        plural={tableSelection?.selection?.length > 1}
                    />
                    <Button
                        component={Link}
                        to={buildCreatePageTarget(functionName)}
                        variant="contained"
                        color="primary"
                    >
                        <Trans>Create</Trans>
                    </Button>
                    <ImportDialog gridRefresherFunc={refresher} />
                    <Button variant="contained" color="primary" onClick={handleExport}>
                        <Trans>Export</Trans>
                    </Button>
                    {!isIpmDisabled && (
                        <Button
                            variant="contained"
                            color="primary"
                            component={Link}
                            to={buildRootPageTarget(AdminPages.WanInterfaces)}
                        >
                            <Trans>WAN Interface</Trans>
                        </Button>
                    )}
                </Actions>
            }
        >
            <DataTable
                tableId="admin-site-list"
                loader={loader}
                triggerRef={triggerRef}
                columns={getColumns(classes)}
                visibleRows={0}
                showSelectionColumn
                sorters={initialSort}
                onStateChanged={setTableState}
                selectionOptions={{
                    mode: SELECTIONMODE_MULTI,
                    allowSelectAll: true,
                    idMapping: '_id',
                    onChange: handleTableSelectionChange,
                }}
            />
        </AdminPage>
    );
}
